import { addFilter, type FilterCallback } from "@/extensions/hooks";
import type { FilterMap, QuoteSummaryColumn } from "@/extensions/hook-map";
import { findProductForItem } from "@/utils/configurator-utility/quote-summary-products";

const NAMESPACE = "duke/rules/quote-summary-table-update";

type RowContext = FilterMap["quoteSummary.row"]["context"];

export interface QuoteSummaryColumnRule {
  column: QuoteSummaryColumn;
  // Key of the column to insert before; appended at the end when missing
  before?: string;
  getValue: (context: RowContext) => string | null | undefined;
}

export const QUOTE_SUMMARY_COLUMN_RULES: QuoteSummaryColumnRule[] = [
  {
    column: { key: "description", label: "Description", width: 240 },
    before: "price",
    getValue: ({ item, products }) =>
      findProductForItem(item.sku, products, (product) => product.description)?.description,
  },
];

const insertColumn = (columns: QuoteSummaryColumn[], rule: QuoteSummaryColumnRule) => {
  const beforeIndex = rule.before
    ? columns.findIndex((column) => column.key === rule.before)
    : -1;
  const insertAt = beforeIndex === -1 ? columns.length : beforeIndex;
  return [...columns.slice(0, insertAt), rule.column, ...columns.slice(insertAt)];
};

const addRuleColumns: FilterCallback<"quoteSummary.columns"> = (columns) =>
  QUOTE_SUMMARY_COLUMN_RULES.reduce(insertColumn, columns);

const fillRuleColumnValues: FilterCallback<"quoteSummary.row"> = (row, context) =>
  QUOTE_SUMMARY_COLUMN_RULES.reduce(
    (updatedRow, rule) => ({
      ...updatedRow,
      [rule.column.key]: rule.getValue(context) || "-",
    }),
    row,
  );

export const registerQuoteSummaryTableUpdate = () => {
  const removeColumnFilter = addFilter("quoteSummary.columns", NAMESPACE, addRuleColumns);
  const removeRowFilter = addFilter("quoteSummary.row", NAMESPACE, fillRuleColumnValues);

  return () => {
    removeColumnFilter();
    removeRowFilter();
  };
};
